/**
 * BulkBookingSmsModal — iter143
 * ─────────────────────────────────────────────────────────────────────────
 * Bulk message to selected bookings via SMS or Email. User asks:
 *   • "popup size too small" → bumped from max-w-3xl → max-w-7xl (~80%
 *     of a 1920px laptop, mirroring the booking widget popup).
 *   • "SMS and Email template BOTH SHOULD APPEAR TO CHOOSE" → new channel
 *     toggle at the top. Switches the template list AND the editor.
 *   • "templates were showing service notes" → backend /api/templates
 *     route now honours `?template_type=...`. We pass 'sms' or 'email'
 *     based on the active channel.
 *
 * Channels:
 *   SMS   → POST /api/sms/bulk-bookings           (160-char text)
 *   Email → POST /api/sms/bulk-bookings-email     (HTML body + subject)
 *
 * Preview (POST /api/sms/bulk-bookings/preview) returns BOTH to_mobile +
 * to_email per recipient, so the recipient list can show the right
 * column based on channel.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, MessageSquare, Mail, Send, AlertTriangle, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BulkBookingSmsModal({ open, onOpenChange, bookingIds, onSent }) {
  const idArray = useMemo(
    () => (bookingIds instanceof Set ? Array.from(bookingIds) : (bookingIds || [])),
    [bookingIds],
  );

  // iter143 — Channel toggle. 'sms' (default) or 'email'.
  const [channel, setChannel] = useState('sms');

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');                  // SMS body / Email plain alt
  const [bodyHtml, setBodyHtml] = useState('');          // Email rich HTML
  const [subject, setSubject] = useState('');           // Email subject
  const [recipients, setRecipients] = useState([]);
  const [firstRendered, setFirstRendered] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset volatile state when the modal opens or channel switches
  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setBody('');
    setBodyHtml('');
    setSubject('');
  }, [open, channel]);

  // Load templates for the active channel
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/templates?template_type=${channel}`, {
          credentials: 'include',
        });
        if (r.ok) {
          const j = await r.json();
          if (!cancelled) setTemplates(j.templates || []);
        }
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [open, channel]);

  // Resolve recipient cohort + first render.
  // PERF (iter231) — debounced: the effect depends on the live editor body,
  // so every keystroke used to refire a full cohort resolve. 450ms settle.
  useEffect(() => {
    if (!open) return undefined;
    if (!idArray.length) {
      setRecipients([]);
      setFirstRendered('');
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const previewBody = channel === 'sms' ? body : (bodyHtml || subject || '');
        const r = await fetch(`${BACKEND_URL}/api/sms/bulk-bookings/preview`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_ids: idArray,
            template_id: templateId || undefined,
            body_override: previewBody || undefined,
          }),
        });
        if (r.ok) {
          const j = await r.json();
          if (!cancelled) {
            setRecipients(j.recipients || []);
            setFirstRendered(j.first_rendered || '');
          }
        }
      } catch (_e) { /* noop */ } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, idArray, templateId, body, bodyHtml, subject, channel]);

  // Apply a picked template to the editor
  const applyTemplate = (tplId) => {
    setTemplateId(tplId);
    if (!tplId) return;
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    const content = tpl.content || {};
    if (channel === 'sms') {
      setBody(content.body || tpl.body || '');
    } else {
      setSubject(content.subject || '');
      setBodyHtml(content.body || content.html || tpl.body || '');
    }
  };

  const reachableCount = useMemo(() => recipients.filter(
    (r) => !r._missing && (channel === 'sms' ? r.has_mobile : r.has_email),
  ).length, [recipients, channel]);
  const unreachableCount = recipients.length - reachableCount;

  const canSend = useMemo(() => {
    if (sending) return false;
    if (reachableCount === 0) return false;
    if (channel === 'sms') return !!body.trim();
    return !!(bodyHtml || '').trim() && !!subject.trim();
  }, [sending, reachableCount, channel, body, bodyHtml, subject]);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const path = channel === 'sms' ? 'bulk-bookings' : 'bulk-bookings-email';
      const payload = channel === 'sms'
        ? {
            booking_ids: idArray,
            template_id: templateId || undefined,
            body_override: body,
            per_customer_merge: true,
          }
        : {
            booking_ids: idArray,
            template_id: templateId || undefined,
            subject,
            body_html: bodyHtml,
            per_customer_merge: true,
          };
      const r = await fetch(`${BACKEND_URL}/api/sms/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j?.detail || `Send failed (${r.status})`);
        return;
      }
      const { sent_count = 0, failed_count = 0 } = j;
      const chLabel = channel.toUpperCase();
      if (failed_count > 0 && sent_count > 0) {
        toast.warning(`${chLabel} — Sent ${sent_count} · ${failed_count} failed`);
      } else if (failed_count > 0) {
        toast.error(`${chLabel} — All ${failed_count} sends failed`);
      } else {
        toast.success(`${chLabel} sent to ${sent_count} customer${sent_count === 1 ? '' : 's'}`);
      }
      onSent?.(j);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* iter143 — Bigger popup. max-w-7xl + w-[80vw] gives ~80% of laptop
          width like the booking widget popup the user referenced. */}
      <DialogContent
        className="max-w-7xl w-[80vw] max-h-[90vh] overflow-y-auto"
        data-testid="bulk-sms-modal"
        onPointerDownOutside={(e) => sending && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {channel === 'sms' ? (
              <MessageSquare className="h-5 w-5 text-indigo-600" />
            ) : (
              <Mail className="h-5 w-5 text-blue-600" />
            )}
            Send {channel === 'sms' ? 'SMS' : 'Email'} to {idArray.length} booking{idArray.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Pick a {channel === 'sms' ? 'SMS' : 'Email'} template or write your own.
            Merge tags like
            <code className="mx-1 px-1 bg-slate-100 rounded text-[11px]">{'{{customer_first_name}}'}</code>
            <code className="mx-1 px-1 bg-slate-100 rounded text-[11px]">{'{{vehicle_rego}}'}</code>
            <code className="mx-1 px-1 bg-slate-100 rounded text-[11px]">{'{{scheduled_date}}'}</code>
            render per customer.
          </DialogDescription>
        </DialogHeader>

        {/* iter143 — Channel toggle */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit" data-testid="bulk-channel-toggle">
          <button
            type="button"
            onClick={() => setChannel('sms')}
            className={`px-4 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition ${
              channel === 'sms'
                ? 'bg-white shadow text-indigo-700'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="bulk-channel-sms"
          >
            <MessageSquare className="h-3.5 w-3.5" /> SMS
          </button>
          <button
            type="button"
            onClick={() => setChannel('email')}
            className={`px-4 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition ${
              channel === 'email'
                ? 'bg-white shadow text-blue-700'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="bulk-channel-email"
          >
            <Mail className="h-3.5 w-3.5" /> Email
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Left: template + editor */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Template</Label>
              <Select value={templateId || 'none'} onValueChange={(v) => applyTemplate(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="bulk-sms-template-select">
                  <SelectValue placeholder="Pick a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— write from scratch —</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name || t.title || t.template_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-[10px] text-slate-400 mt-1">
                  No {channel.toUpperCase()} templates yet — manage them in Super Admin → Templates.
                </p>
              )}
            </div>

            {channel === 'email' && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Your booking with us tomorrow"
                  className="text-sm"
                  data-testid="bulk-email-subject"
                />
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                {channel === 'sms' ? 'Message body' : 'Body (HTML allowed)'}
              </Label>
              {channel === 'sms' ? (
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  placeholder="Hi {{customer_first_name}}, you're booked in for {{scheduled_date}}…"
                  className="text-sm font-mono"
                  data-testid="bulk-sms-body"
                />
              ) : (
                <RichEmailEditor value={bodyHtml} onChange={setBodyHtml} />
              )}
              <p className="text-[10px] text-slate-400 mt-1">
                {channel === 'sms'
                  ? `${body.length}/640 chars · ${Math.ceil((body.length || 1) / 160)} SMS part(s) per recipient`
                  : 'Rich HTML supported. Plain-text alt is auto-generated.'}
              </p>
            </div>

            <div className={`rounded-md border p-3 ${channel === 'sms' ? 'border-indigo-200 bg-indigo-50/40' : 'border-blue-200 bg-blue-50/40'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${channel === 'sms' ? 'text-indigo-700' : 'text-blue-700'}`}>
                Preview · first recipient
              </p>
              {firstRendered ? (
                channel === 'email' ? (
                  <div
                    className="text-xs text-slate-900"
                    data-testid="bulk-sms-preview-rendered"
                    dangerouslySetInnerHTML={{ __html: firstRendered }}
                  />
                ) : (
                  <pre
                    className="text-xs text-slate-900 whitespace-pre-wrap font-sans"
                    data-testid="bulk-sms-preview-rendered"
                  >
                    {firstRendered}
                  </pre>
                )
              ) : previewLoading ? (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> resolving…
                </p>
              ) : (
                <p className="text-xs text-slate-400 italic">Body is empty</p>
              )}
            </div>
          </div>

          {/* Right: recipient list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Recipients ({recipients.length})
              </Label>
              <div className="flex items-center gap-2">
                {reachableCount > 0 && (
                  <span className="text-[10px] inline-flex items-center gap-1 text-emerald-700">
                    <Check className="h-3 w-3" /> {reachableCount} reachable
                  </span>
                )}
                {unreachableCount > 0 && (
                  <span className="text-[10px] inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> {unreachableCount} no {channel === 'sms' ? 'mobile' : 'email'}
                  </span>
                )}
              </div>
            </div>
            <div
              className="max-h-[55vh] overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100"
              data-testid="bulk-sms-recipient-list"
            >
              {recipients.length === 0 && previewLoading && (
                <p className="text-xs text-slate-500 p-3 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Resolving customers…
                </p>
              )}
              {recipients.length === 0 && !previewLoading && (
                <p className="text-xs text-slate-400 italic p-3">No recipients</p>
              )}
              {recipients.map((r) => {
                const reachable = channel === 'sms' ? r.has_mobile : r.has_email;
                const dest = channel === 'sms' ? r.to_mobile : r.to_email;
                return (
                  <div
                    key={r.booking_id}
                    className={`px-3 py-2 text-xs flex items-center justify-between gap-2 ${(!reachable || r._missing) ? 'bg-amber-50/60' : ''}`}
                    data-testid={`bulk-sms-recipient-${r.booking_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate">
                        {r.customer_name || '(no name)'}
                        {r.vehicle_rego && (
                          <span className="text-slate-500 font-normal ml-1">· {r.vehicle_rego}</span>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {r.scheduled_date || 'date tbd'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      {reachable ? (
                        <code className="text-[11px] text-slate-700">{dest}</code>
                      ) : r._missing ? (
                        <span className="text-[10px] text-rose-700">not found</span>
                      ) : (
                        <span className="text-[10px] text-amber-700">no {channel === 'sms' ? 'mobile' : 'email'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={sending}
            data-testid="bulk-sms-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className={channel === 'sms' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
            data-testid="bulk-sms-send"
          >
            {sending ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-3 w-3 mr-1" /> Send to {reachableCount} customer{reachableCount === 1 ? '' : 's'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * RichEmailEditor — iter143
 * Minimal contentEditable-based WYSIWYG. No external dep. Supports the
 * common shortcuts SM cares about: bold (Cmd/Ctrl+B), italic, underline,
 * link insertion, bullet list. Pastes HTML; emits clean HTML on change.
 * Good enough for "your appointment tomorrow" templated emails — not a
 * full Word replacement (we'll wire TipTap if user needs heavy
 * formatting later).
 */
function RichEmailEditor({ value, onChange }) {
  // Use refs to avoid re-mounting the contentEditable on every keystroke
  // (which would jump the cursor).
  const ref = (el) => {
    if (!el) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  };

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
  };

  const handleInput = (e) => {
    onChange(e.currentTarget.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (https://…)');
    if (!url) return;
    exec('createLink', url);
  };

  return (
    <div className="border border-slate-300 rounded-md overflow-hidden bg-white">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-slate-200 bg-slate-50">
        <ToolbarBtn onClick={() => exec('bold')} title="Bold (⌘B)"><b>B</b></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Italic (⌘I)"><i>I</i></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Underline (⌘U)"><u>U</u></ToolbarBtn>
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list">• ≡</ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Number list">1.</ToolbarBtn>
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={insertLink} title="Insert link">🔗</ToolbarBtn>
        <ToolbarBtn onClick={() => exec('removeFormat')} title="Clear formatting">Tx</ToolbarBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={handleInput}
        className="min-h-[180px] p-3 text-sm focus:outline-none prose prose-sm max-w-none"
        data-testid="bulk-email-body-html"
        suppressContentEditableWarning
      />
    </div>
  );
}

function ToolbarBtn({ onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}  // keep selection while clicking
      onClick={onClick}
      title={title}
      className="px-2 py-0.5 rounded text-xs hover:bg-slate-200 text-slate-700"
    >
      {children}
    </button>
  );
}
